import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import Link from '@atlaskit/link';
import Avatar from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { useProfileInfo } from '../useProfileInfo.tsx';
import { i18n } from '../profileMacrosi18n.ts';
export type ProfilePictureMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: {
		User?: {
			value?: string;
		};
		Size?: {
			value?: string;
		};
	};
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProfilePictureContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	display: 'flex !important',
});

// Adding padding so that user has a touch target, otherwise everytime they click the macro,
// it will open the link to the profile.
const containerStyles = xcss({
	padding: 'space.075',
	display: 'inline-block',
});

// Bumping up the size by 1 for each to match original sizes
export const getAvatarSize = (macroSize?: string) => {
	switch (macroSize) {
		case 'extra large':
			return 'xxlarge';
		case 'large':
			return 'xlarge';
		default:
			return 'large';
	}
};

const ProfilePictureMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: ProfilePictureMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const userId = macroParams?.User?.value;
	const macroSize = macroParams?.Size?.value;
	const { accessStatus } = useSessionData();

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	const { hasViewUserProfilePermission, graphqlError, userName, iconUrl } = useProfileInfo(userId);

	const profileUrl = hasViewUserProfilePermission ? getAtlassianPeopleProfileUrl(userId) : '';
	const altText = `${formatMessage(i18n.imageAlt)}: ${userName}`;
	const dataMacroParameters = {
		User: userId,
		Size: macroSize,
	};
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const getAvatar = () => {
		// When trying to preload the query which fetches the profile info it is timing out :(
		// The workaround is just to use placeholders for the avatars in SSR and not show anything like before if the
		// user hasn't selected someone to view for the profile.
		if (renderServerPlaceholder) {
			return userId ? (
				<Avatar appearance="circle" size={getAvatarSize(macroSize)} testId="placeholder-avatar" />
			) : null;
		}

		return (
			<Link testId="profile-link" href={profileUrl} target="_blank">
				<ProfileAvatar
					userId={userId}
					src={iconUrl}
					size={getAvatarSize(macroSize)}
					name={altText}
					isAnonymous={isAnonymousUser}
					testId="profile-image"
				/>
			</Link>
		);
	};

	return (
		<Box
			xcss={containerStyles}
			data-fabric-macro={macroDefaultProps?.attributes?.macroId}
			data-macro-body=""
			data-macro-parameters={JSON.stringify(dataMacroParameters)}
		>
			<ProfilePictureContainer
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="userLogoLink"
				data-username={userId}
				data-account-id={userId}
			>
				{graphqlError && <ErrorDisplay error={graphqlError} />}
				{getAvatar()}
			</ProfilePictureContainer>
		</Box>
	);
};

export const ProfilePictureMacroRenderer = (props: ProfilePictureMacroRendererProps) => {
	return (
		<GenericErrorBoundary
			renderOnError={() => <FormattedMessage {...i18n.error} />}
			attribution={Attribution.PAGE_EXTENSIONS}
		>
			<ProfilePictureMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
